import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Gauge, TrendingDown, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { band, extractScore } from "@/components/ResultScore";
import { celebrate } from "@/components/ScoreToast";
import { useAuth } from "@/context/AuthContext";
import { api, asList, enc, tierRank } from "@/lib/api";
import { getHistory, recordScore } from "@/lib/atsScores";
import { findAts, loadTools } from "@/lib/tools";
import { autoRecheckOn } from "@/components/profile/AtsAutomation";

const MAX_JOBS = 10;
const SKIP = ["rejected", "offer"];

const Row = ({ r, stopped }) => {
  const d = r.prev != null && r.score != null ? r.score - r.prev : null;
  return (
    <li className="flex items-center gap-2 text-xs" data-testid={`recheck-row-${r.jobId}`}>
      <span className="min-w-0 flex-1 truncate text-zinc-300">{r.title}</span>
      {r.score == null ? <span className="text-zinc-600">{r.error ? "failed" : stopped ? "skipped" : "..."}</span> : (
        <>
          <span className="font-mono" style={{ color: band(r.score).c }}>{r.score}%</span>
          {d != null && d !== 0 && <span className={`flex items-center font-mono ${d > 0 ? "text-emerald-400" : "text-red-400"}`}>{d > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{d > 0 ? "+" : ""}{d}</span>}
        </>
      )}
    </li>
  );
};

export const AutoRecheck = () => {
  const { tier } = useAuth();
  const [st, setSt] = useState(null);
  const [min, setMin] = useState(false);
  const cancel = useRef(false);

  useEffect(() => {
    const onReplaced = async (e) => {
      if (!autoRecheckOn()) return;
      const skip = e.detail?.skipJobId;
      const tools = await loadTools();
      const ats = findAts(tools?.tools);
      if (!ats || tierRank(ats.tier) > tierRank(tools?.tier || tier)) return;
      let apps = [];
      try { apps = asList((await api.get("/applications")).data, "applications"); } catch { return; }
      const jobs = apps.filter((a) => a.job_id && a.job_id !== skip && !SKIP.includes(a.status)).slice(0, MAX_JOBS)
        .map((a) => ({ jobId: a.job_id, title: a.job?.title || a.job_id, prev: getHistory(a.job_id).at(-1)?.score ?? null, score: null }));
      if (!jobs.length) return;
      cancel.current = false;
      setSt({ running: true, rows: jobs });
      for (let i = 0; i < jobs.length; i++) {
        if (cancel.current) break;
        const j = jobs[i];
        try {
          const { data } = await api.post(`/ai/tools/${ats.id}`, { job_id: j.jobId, inputs: {} });
          const s = extractScore(data, true);
          if (s != null) { recordScore(j.jobId, s, ats.id); j.score = Math.round(s); } else j.error = true;
        } catch { j.error = true; }
        setSt({ running: true, rows: [...jobs] });
      }
      setSt({ running: false, rows: [...jobs], cancelled: cancel.current });
      const best = jobs.filter((j) => j.prev != null && j.score != null).sort((a, b) => (b.score - b.prev) - (a.score - a.prev))[0];
      if (best && best.score > best.prev) celebrate(best.score - best.prev, best.score);
    };
    window.addEventListener("rpopro:resume-replaced", onReplaced);
    return () => window.removeEventListener("rpopro:resume-replaced", onReplaced);
  }, [tier]);

  if (!st) return null;
  const done = st.rows.filter((r) => r.score != null || r.error).length;
  const improved = st.rows.filter((r) => r.prev != null && r.score != null && r.score > r.prev).length;
  return (
    <div className="fade-up pointer-events-auto fixed bottom-20 right-4 z-[60] lg:bottom-4 w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl border border-[#D4AF37]/40 bg-[#18181B]/95 p-4 shadow-2xl backdrop-blur-md" data-testid="auto-recheck-panel">
      <div className="mb-2 flex items-center gap-2">
        <Gauge className="h-4 w-4 text-[#D4AF37]" />
        <span className="flex-1 font-display text-sm text-zinc-100" data-testid="auto-recheck-status">
          {st.running ? `Re-checking your jobs with the new resume · ${done}/${st.rows.length}` : st.cancelled ? `Stopped · ${done}/${st.rows.length} re-checked` : `Done · ${improved} of ${st.rows.length} improved`}
        </span>
        <button onClick={() => setMin((m) => !m)} className="text-zinc-500 hover:text-zinc-200" title={min ? "Expand" : "Minimize"} data-testid="auto-recheck-minimize">{min ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        {st.running ? <Spinner className="text-[#D4AF37]" /> : <button onClick={() => setSt(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="auto-recheck-close"><X className="h-4 w-4" /></button>}
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500" style={{ width: `${(done / st.rows.length) * 100}%` }} /></div>
      {!min && <ul className="mt-3 max-h-48 space-y-1.5 overflow-y-auto">{st.rows.map((r) => <Row key={r.jobId} r={r} stopped={!st.running} />)}</ul>}
      {st.running && !min && <Button size="sm" variant="ghost" onClick={() => { cancel.current = true; }} className="mt-2 w-full text-zinc-400" data-testid="auto-recheck-cancel">Stop</Button>}
    </div>
  );
};
